const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

interface RequestOptions extends RequestInit {
  token?: string | null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { token, headers: extraHeaders, ...rest } = options;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...extraHeaders,
  };

  if (token != null) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, { headers, ...rest });
  const json = (await res.json()) as { success?: boolean; data?: T; error?: { code: string; message: string } };

  // Not every route wraps its response in { success, data } — some return the
  // payload directly (e.g. { items, total }). Only treat an explicit
  // `success: false` as an error; a missing field alongside a 2xx status is OK.
  if (!res.ok || json.success === false) {
    throw new ApiError(
      res.status,
      json.error?.code ?? "UNKNOWN",
      json.error?.message ?? "An unexpected error occurred",
    );
  }

  return ("data" in json ? json.data : json) as T;
}
